import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
const componentName = 'Popover';
const classPrefix = `ui-Popover`;
export const popoverClasses = {
    root: `${classPrefix}-root-${randomStr()}`,
    paper: `${classPrefix}-paper`,
    arrow: `${classPrefix}-arrow`,
    title: `${classPrefix}-title`,
    closeIcon: `${classPrefix}-closeIcon`,
    content: `${classPrefix}-content`,
    footer: `${classPrefix}-footer`,
    okButton: `${classPrefix}-okButton`,
};
export function getStyle(originTheme, opts) {
    const { width, maxWidth, closable, title, content, mode } = opts;
    // Overwrite the mode when the theme and mode are different
    const needToOverwriteMode = typeof mode !== 'undefined' && mode !== originTheme?.palette?.mode;
    const theme = {
        ...originTheme,
    };
    if (needToOverwriteMode) {
        theme.palette = {
            ...theme.palette,
            ...{ mode: mode === 'dark' ? 'dark' : 'light' },
        };
    }
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        [`&.${popoverClasses.root}`]: {
            zIndex: tk(token.zIndex),
            [`.${popoverClasses.paper}`]: {
                width: width ?? tk(token.width),
                maxWidth: maxWidth ?? tk(token.maxWidth),
                padding: tk(token.padding),
                background: tk(token.background),
                boxShadow: tk(token.boxShadow),
                borderStyle: 'solid',
                borderWidth: 1,
                borderColor: tk(token.border),
                borderRadius: tk(token.borderRadius),
                position: 'relative',
                fontSize: '16px',
            },
            [`.${popoverClasses.arrow}`]: {
                overflow: 'hidden',
                position: 'absolute',
                width: '1em',
                height: '0.71em' /* = width / sqrt(2) = (length of the hypotenuse) */,
                boxSizing: 'border-box',
                color: tk(token.arrow.color),
                '&::before': {
                    content: '""',
                    margin: 'auto',
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'currentColor',
                    borderStyle: 'solid',
                    borderWidth: 1,
                    borderColor: tk(token.arrow.border),
                    transform: 'rotate(45deg)',
                    borderRadius: 3,
                },
            },
            [`&[data-popper-placement*="bottom"] .${popoverClasses.arrow}`]: {
                top: 0,
                left: 0,
                marginTop: '-0.71em',
                '&::before': {
                    transformOrigin: '0 100%',
                },
            },
            [`&[data-popper-placement*="top"] .${popoverClasses.arrow}`]: {
                bottom: 0,
                left: 0,
                marginBottom: '-0.71em',
                '&::before': {
                    transformOrigin: '100% 0',
                },
            },
            [`&[data-popper-placement*="right"] .${popoverClasses.arrow}`]: {
                left: 0,
                marginInlineStart: '-0.71em',
                height: '1em',
                width: '0.71em',
                '&::before': {
                    transformOrigin: '100% 100%',
                },
            },
            [`&[data-popper-placement*="left"] .${popoverClasses.arrow}`]: {
                right: 0,
                marginInlineEnd: '-0.71em',
                height: '1em',
                width: '0.71em',
                '&::before': {
                    transformOrigin: '0 0',
                },
            },
            [`.${popoverClasses.title}`]: {
                display: 'flex',
                color: tk(token.title.color),
                fontWeight: tk(token.title.fontWeight),
                lineHeight: tk(token.title.lineHeight),
                fontFamily: tk(token.fontFamily),
                fontSize: tk(token.fontSize),
                marginBottom: tk(token.title.marginBottom),
                ...(closable && { paddingRight: tk(token.title.paddingRight) }),
            },
            [`.${popoverClasses.closeIcon}`]: {
                position: tk(token.closeIcon.position),
                right: tk(token.closeIcon.right),
                top: tk(token.closeIcon.top),
            },
            [`.${popoverClasses.content}`]: {
                color: tk(token.content.color),
                lineHeight: tk(token.content.lineHeight),
                fontFamily: tk(token.fontFamily),
                fontWeight: tk(token.content.fontWeight),
                fontSize: tk(token.fontSize),
                ...(closable &&
                    !title && { paddingRight: tk(token.content.paddingRight) }),
            },
            [`.${popoverClasses.footer}`]: {
                marginTop: tk(token.footer.marginTop),
                display: 'flex',
                justifyContent: 'flex-end',
                fontFamily: tk(token.fontFamily),
                ...(closable &&
                    !title &&
                    !content && { paddingRight: tk(token.footer.paddingRight) }),
                [`.${popoverClasses.okButton}`]: {
                    marginInlineStart: tk(token.footer.ok.marginLeft),
                },
            },
        },
    };
    return style;
}
export function getBackdropStyle(theme) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        zIndex: tk(token.zIndex),
        background: 'transparent',
    };
    return style;
}
